// Pure state rules behind CostingSheet (audit/RECIPE_BUILDER_AUDIT_2026-09-14.md
// U-1, U-2, U-3, U-5, U-11, U-15, U-16).
//
// The sheet lost work in four ways on dev: a manually costed item always read
// "dirty" (the baseline and the live value were built by different rules); a
// refetch replaced the draft mid-edit; a Customise toggle rebuilt an option
// with no rows and Save wiped it for every product sharing the group; and
// Prev / Next walked a list that re-sorted under it. Each rule that decides
// those outcomes is here, as a pure function, so it is tested rather than
// rediscovered.

import type { CostMode } from '@/lib/productFormPayload'
import {
  buildRecipePayload,
  fromServerComponents,
  multiplierValid,
  type RecipeLineDraft,
  type RecipeLineMode,
  type RecipeScope,
  type VariantDraft,
} from '@/lib/recipeEditor'
import type { ProductRecipe, RecipeVariant } from '@/types'

export interface CostingSource {
  recipe: ProductRecipe
  projected_cost?: number | null
  projected_cost_basis?: 'amount' | 'percent' | null
  projected_cost_percent?: number | null
}

export interface SheetDraft {
  mode: CostMode
  lines: RecipeLineDraft[]
  yieldStr: string
  amount: string
  percent: string
  /** Option id → draft. Absent = "same as base" (no recipe of its own). */
  variants: Record<string, VariantDraft>
}

/** The draft a product opens with — also the baseline dirty-checks compare to. */
export function initialDraft(src: CostingSource): SheetDraft {
  const { recipe } = src
  const hasRecipe = recipe.components.length > 0
  const mode: CostMode = hasRecipe
    ? 'recipe'
    : src.projected_cost_basis === 'percent'
      ? 'percent'
      : src.projected_cost != null
        ? 'amount'
        : 'none'
  const variants: Record<string, VariantDraft> = {}
  for (const v of recipe.variants) {
    const saved = variantDraftFromSaved(v)
    if (saved) variants[v.option_id] = saved
  }
  return {
    mode,
    lines: fromServerComponents(recipe.components, recipe.recipe_yield),
    yieldStr: String(recipe.recipe_yield || 1),
    amount: src.projected_cost != null && src.projected_cost_basis !== 'percent' ? String(src.projected_cost) : '',
    percent: src.projected_cost_percent != null ? String(src.projected_cost_percent) : '',
    variants,
  }
}

function stripKey(l: RecipeLineDraft) {
  const { key: _k, ...rest } = l
  void _k
  return rest
}

/**
 * One string per draft, built by ONE rule for both the baseline and the live
 * value (U-5): amount only counts in amount mode, percent only in percent
 * mode, the yield as a number when it is one. Variants are compared
 * separately (changedVariantIds) because they are saved separately.
 */
export function draftSnapshot(d: Pick<SheetDraft, 'mode' | 'lines' | 'yieldStr' | 'amount' | 'percent'>): string {
  const y = Number(d.yieldStr)
  return JSON.stringify({
    m: d.mode,
    l: d.lines.map(stripKey),
    y: d.yieldStr.trim() !== '' && y > 0 ? y : d.yieldStr.trim(),
    a: d.mode === 'amount' ? d.amount.trim() : '',
    p: d.mode === 'percent' ? d.percent.trim() : '',
  })
}

/** Everything server-side the draft was built from; a change means "refetched with news". */
export function serverSignature(src: CostingSource): string {
  return JSON.stringify({
    r: src.recipe,
    c: src.projected_cost ?? null,
    b: src.projected_cost_basis ?? null,
    p: src.projected_cost_percent ?? null,
  })
}

/**
 * What to do when the server data behind an open sheet changes (U-2).
 *  - a different product, or nothing loaded yet → adopt
 *  - a change we caused ourselves → 'adopt' after a save, 'rebase' after
 *    "Remove it" / "Set cost" (move the baseline, keep what is being typed)
 *  - someone else's change while the draft is dirty → 'stale' (show a banner,
 *    never wipe the draft)
 *  - otherwise → adopt silently
 */
export type ServerChangeAction = 'adopt' | 'rebase' | 'stale' | 'ignore'

export function onServerChange(args: {
  loadedForId: string | null
  productId: string
  appliedSignature: string | null
  signature: string
  dirty: boolean
  expecting: 'adopt' | 'rebase' | null
}): ServerChangeAction {
  if (args.loadedForId !== args.productId || args.appliedSignature == null) return 'adopt'
  if (args.signature === args.appliedSignature) return 'ignore'
  if (args.expecting) return args.expecting
  return args.dirty ? 'stale' : 'adopt'
}

// ── Variants ────────────────────────────────────────────────────────────────

/** An option's saved recipe as a draft, or null when it has none. */
export function variantDraftFromSaved(v: RecipeVariant): VariantDraft | null {
  if (!v.has_recipe) return null
  return { multiplier: String(v.recipe_multiplier || 1), lines: fromServerComponents(v.components, 1), onlyThisItem: Boolean(v.own_recipe) }
}

/** The draft Customise ON starts from: the saved recipe, never an empty one (U-1). */
export function customiseDraft(v: RecipeVariant): VariantDraft {
  return variantDraftFromSaved(v) ?? { multiplier: String(v.recipe_multiplier || 1), lines: [] }
}

function variantShape(d: VariantDraft | null | undefined): string {
  if (!d) return JSON.stringify({ m: 1, l: [], own: false })
  const rows = d.lines.map(stripKey).filter((l) => l.stock_item_id || l.qty.trim() !== '')
  const m = Number(d.multiplier)
  return JSON.stringify({ m: d.multiplier.trim() !== '' && m > 0 ? m : d.multiplier.trim(), l: rows, own: Boolean(d.onlyThisItem) })
}

/**
 * The options whose draft differs from what is saved. A toggle that ends back
 * where it started does not count, and an empty "Customise" on an option with
 * no recipe is the same as no draft.
 */
export function changedVariantIds(drafts: Record<string, VariantDraft>, variants: RecipeVariant[]): string[] {
  const out: string[] = []
  for (const v of variants) {
    if (variantShape(drafts[v.option_id]) !== variantShape(variantDraftFromSaved(v))) out.push(v.option_id)
  }
  return out
}

export interface SharedChange {
  optionId: string
  optionName: string
  groupName: string
  productCount: number
  removes: boolean
}

/** Changed options whose recipe is shared by more than one item — each needs confirming. */
export function sharedChanges(
  changedIds: string[],
  variants: RecipeVariant[],
  drafts: Record<string, VariantDraft>,
): SharedChange[] {
  const out: SharedChange[] = []
  for (const id of changedIds) {
    const v = variants.find((x) => x.option_id === id)
    if (!v || (v.group_product_count ?? 0) <= 1) continue
    // F-MOD-N13: a change confined to this item's own recipe — saving one, or
    // dropping one to fall back on the shared recipe — touches no other item.
    if (drafts[id]?.onlyThisItem || (!drafts[id] && v.own_recipe)) continue
    out.push({
      optionId: id,
      optionName: v.option_name,
      groupName: v.group_name,
      productCount: v.group_product_count,
      removes: !drafts[id] && v.has_recipe,
    })
  }
  return out
}

export interface OptionRecipeBody {
  recipe_multiplier: number
  components: Array<{
    stock_item_id: string
    deduct_qty_entered: number
    deduct_unit: string
    order_type_scope: RecipeScope
    mode: RecipeLineMode
  }>
}

/**
 * The body an option PUT sends for a draft (null = remove its recipe). The
 * multiplier goes as typed: Save is blocked on an invalid one rather than it
 * being turned into 1 behind the owner's back (U-22).
 */
export function optionRecipeBody(d: VariantDraft | null | undefined): OptionRecipeBody {
  if (!d) return { recipe_multiplier: 1, components: [] }
  return {
    recipe_multiplier: Number(d.multiplier),
    components: buildRecipePayload(d.lines, 1, 'option').map((c) => ({
      stock_item_id: c.stock_item_id,
      deduct_qty_entered: c.deduct_qty_entered,
      deduct_unit: c.deduct_unit,
      order_type_scope: c.order_type_scope,
      mode: c.mode ?? 'add',
    })),
  }
}

/**
 * The option PUTs one Save makes for an option (F-MOD-N13). `productScoped`
 * true writes this item's own recipe; an empty scoped write drops the item back
 * onto the shared recipe. Moving an item off its own recipe onto an edited
 * shared one is two writes: the shared recipe, then the item's own cleared.
 */
export function optionSaveWrites(
  draft: VariantDraft | null | undefined,
  savedOwn: boolean,
): Array<{ productScoped: boolean; body: OptionRecipeBody }> {
  const empty: OptionRecipeBody = { recipe_multiplier: 1, components: [] }
  if (draft?.onlyThisItem) return [{ productScoped: true, body: optionRecipeBody(draft) }]
  if (!draft && savedOwn) return [{ productScoped: true, body: empty }]
  const writes = [{ productScoped: false, body: optionRecipeBody(draft) }]
  if (savedOwn) writes.push({ productScoped: true, body: empty })
  return writes
}

/** The price an item sells for with this option chosen — the cap its rows are guarded against. */
export function optionPrice(v: Pick<RecipeVariant, 'price_mode' | 'price_delta'>, basePrice: number): number {
  return v.price_mode === 'replace' ? Number(v.price_delta) || 0 : (Number(basePrice) || 0) + (Number(v.price_delta) || 0)
}

// ── Navigation (U-11) ───────────────────────────────────────────────────────

/**
 * The order Prev / Next walks, frozen when the sheet opens. Saving under a
 * filter moves the item out of the live list; walking the snapshot keeps the
 * owner's place. A deep-linked item not in the list goes first.
 */
export function walkSnapshot(filteredIds: string[], currentId: string): string[] {
  return filteredIds.includes(currentId) ? [...filteredIds] : [currentId, ...filteredIds]
}

export function neighbours(snapshot: string[], currentId: string): { prevId: string | null; nextId: string | null } {
  const i = snapshot.indexOf(currentId)
  if (i < 0) return { prevId: null, nextId: snapshot[0] ?? null }
  return {
    prevId: i > 0 ? snapshot[i - 1] : null,
    nextId: i < snapshot.length - 1 ? snapshot[i + 1] : null,
  }
}

// ── Saving (U-15, U-16) ─────────────────────────────────────────────────────

export type SaveStep =
  | { kind: 'base' }
  | { kind: 'manual' }
  | { kind: 'removeRecipe' }
  | { kind: 'option'; optionId: string; label: string }

/**
 * The ordered writes a Save performs. Recipe mode skips the base PUT when
 * there is nothing to write and nothing to clear — so option customisations
 * save on an item with no base recipe. Manual modes save the cost, then remove
 * a recipe that exists. Variants are only saved from recipe mode.
 */
export function planSave(args: {
  mode: CostMode
  basePayloadCount: number
  serverBaseRows: number
  changedOptions: { id: string; label: string }[]
}): SaveStep[] {
  if (args.mode === 'recipe') {
    const steps: SaveStep[] = []
    if (args.basePayloadCount > 0 || args.serverBaseRows > 0) steps.push({ kind: 'base' })
    for (const o of args.changedOptions) steps.push({ kind: 'option', optionId: o.id, label: o.label })
    return steps
  }
  const steps: SaveStep[] = [{ kind: 'manual' }]
  if (args.serverBaseRows > 0) steps.push({ kind: 'removeRecipe' })
  return steps
}

export function stepLabel(step: SaveStep): string {
  switch (step.kind) {
    case 'base':
      return 'the recipe'
    case 'manual':
      return 'the cost'
    case 'removeRecipe':
      return 'removing the recipe'
    case 'option':
      return `the ${step.label} option`
  }
}

/** "Saved the recipe. Not saved: the Iced option — <reason>." */
export function partialSaveMessage(done: SaveStep[], failed: SaveStep, reason: string): string {
  const saved = done.length ? `Saved ${done.map(stepLabel).join(', ')}. ` : ''
  return `${saved}Not saved: ${stepLabel(failed)} — ${reason}`
}

/**
 * Why Save is disabled, in the owner's words, or null when it can run. The
 * footer always shows this when Save is grey (U-15).
 */
export function saveBlockReason(args: {
  pending: boolean
  dirty: boolean
  mode: CostMode
  blockCount: number
  basePayloadCount: number
  serverBaseRows: number
  changedOptionCount: number
  yieldStr: string
  variantMultipliers: string[]
  amount: string
  percent: string
}): string | null {
  if (args.pending) return 'Saving…'
  if (!args.dirty) return 'No changes to save'
  if (args.mode === 'recipe') {
    if (args.blockCount > 0) return `Fix ${args.blockCount} highlighted line${args.blockCount === 1 ? '' : 's'}`
    if (!(args.yieldStr.trim() !== '' && Number(args.yieldStr) > 0)) return 'The recipe has to make at least 1 serving'
    if (args.variantMultipliers.some((m) => !multiplierValid(m))) return 'The multiplier must be above 0'
    if (args.basePayloadCount === 0 && (args.serverBaseRows > 0 || args.changedOptionCount === 0)) {
      return 'Add at least one ingredient, or choose another way to cost this item'
    }
    return null
  }
  if (args.mode === 'amount' && !(args.amount.trim() !== '' && Number(args.amount) >= 0)) return 'Enter a cost of 0 or more'
  if (args.mode === 'percent' && !(args.percent.trim() !== '' && Number(args.percent) >= 0 && Number(args.percent) <= 100)) {
    return 'Enter a percent between 0 and 100'
  }
  return null
}

// -- Option dialog (walkthrough W-2) -----------------------------------------

export type OptionDialogIntent = 'cancel' | 'save'

/**
 * What the option dialog says before it loses unsaved ingredient rows. The
 * Ingredients panel saves on its own, so neither Cancel nor the dialog's own
 * Save carries them.
 */
export function optionDialogLeaveCopy(
  intent: OptionDialogIntent,
  optionName: string,
): { title: string; description: string; confirmLabel: string } {
  if (intent === 'save') {
    return {
      title: `Save ${optionName} without its ingredient changes?`,
      description:
        'Save changes stores the name, price and rules. Ingredient changes are kept only by “Save ingredients”, so save them first or discard them.',
      confirmLabel: 'Discard ingredients and save',
    }
  }
  return {
    title: `Discard ingredient changes to ${optionName}?`,
    description: 'The ingredients have not been saved. Closing now throws those changes away.',
    confirmLabel: 'Discard changes',
  }
}
